// Default texts of the conversational assistant's chat (`chatbot.localization`), per language —
// used by the search dropdown's chat and the assistant modal (./assistant-modal.ts). Merged PER
// KEY: a config setting only `askPlaceholder` keeps the rest from the pack. Pure data + a pure
// merge (see ./locale.ts).
//
// Shipping: only with the assistant's code. The assistant modal imports it directly; the
// dropdown gets it through its chat UI — the facade's `dropdownChat` (../assistant.ts, the
// `@raventic/sdk/assistant` entry) is sdk-assistant's object plus `withChatbotTexts` under
// CHAT_TEXTS_PROPERTY (chatUiWithTexts below), and defineDropdown (./dropdown.ts) fills
// `chatbot.localization` with what the config's `chatbotUi` carries. So a dropdown without the
// chat ships none of these texts (~2.6 kB), and one whose `chatbotUi` isn't the facade's
// `dropdownChat` gets its chat texts unfilled (the SDK's English defaults), as before.
//
// Sources:
//   - cs: the customers' configs (albi-cz, i-zahradninabytek-cz, freshlabels-cz — the most
//     common value per key); `unavailableMessage` drafted (no customer sets it);
//     `escalationMessage` ends with ":" instead of the customers' "." (it introduces the contact
//     links, like sdk-assistant's English).
//   - en: exactly sdk-assistant's own built-in English (what renders when a key is unset), so
//     an English config renders as before.
//   - sk, de: drafted, translated from the cs values (de in the formal "Sie" register, like the
//     cs / sk "vy" one).
//
// `disclaimer` — the small notice centred above the transcript ("You are talking to an AI
// assistant") — is a COMPLIANCE requirement: every chat must say it is an AI. So it is filled like
// the rest, AND a blank value (`""`, whitespace) counts as missing (the assistant renders nothing
// for blank): a config may reword the notice, never switch it off. cs is i-zahradninabytek-cz's
// (and freshlabels-cz's CSS label's) wording; sk, en, de drafted.

import type { ChatbotLocalization } from "@raventic/sdk-search-v1";
import { fillTexts, isPlainObject } from "./locale.ts";
import type { TextLanguage, TextPacks } from "./locale.ts";

export type ChatbotTextKey = keyof ChatbotLocalization;
export type ChatbotTexts = Required<Pick<ChatbotLocalization, ChatbotTextKey>>;

export const CHATBOT_TEXTS = {
  cs: {
    disclaimer: "Mluvíte s AI asistentem",
    askPlaceholder: "Zeptejte se AI asistenta",
    chatPlaceholder: "Zeptejte se na cokoliv dalšího…",
    continueBanner: "Pokračovat v předchozí konverzaci",
    continueBannerDismiss: "Zavřít",
    backToResults: "Zpět na výsledky",
    escalationMessage: "Můžete kontaktovat naši zákaznickou podporu:",
    feedbackQuestion: "Pomohla vám tato odpověď?",
    feedbackYes: "Ano",
    feedbackNo: "Ne",
    deleteConversation: "Smazat konverzaci",
    privacyPolicy: "Ochrana osobních údajů",
    errorMessage: "Něco se pokazilo. Zkuste to prosím znovu.",
    sessionEndedMessage: "Tato konverzace byla ukončena.",
    unavailableMessage: "Asistent je momentálně nedostupný.",
    findSimilar: "Najít podobné",
  },
  sk: {
    disclaimer: "Hovoríte s AI asistentom",
    askPlaceholder: "Opýtajte sa AI asistenta",
    chatPlaceholder: "Opýtajte sa na čokoľvek ďalšie…",
    continueBanner: "Pokračovať v predchádzajúcej konverzácii",
    continueBannerDismiss: "Zavrieť",
    backToResults: "Späť na výsledky",
    escalationMessage: "Môžete kontaktovať našu zákaznícku podporu:",
    feedbackQuestion: "Pomohla vám táto odpoveď?",
    feedbackYes: "Áno",
    feedbackNo: "Nie",
    deleteConversation: "Zmazať konverzáciu",
    privacyPolicy: "Ochrana osobných údajov",
    errorMessage: "Niečo sa pokazilo. Skúste to prosím znova.",
    sessionEndedMessage: "Táto konverzácia bola ukončená.",
    unavailableMessage: "Asistent je momentálne nedostupný.",
    findSimilar: "Nájsť podobné",
  },
  en: {
    disclaimer: "You are talking to an AI assistant",
    askPlaceholder: "Ask anything about these results…",
    chatPlaceholder: "Ask for anything else…",
    continueBanner: "Continue previous conversation",
    continueBannerDismiss: "Close",
    backToResults: "Back to results",
    escalationMessage: "You can contact our customer support directly:",
    feedbackQuestion: "Was this helpful?",
    feedbackYes: "Yes",
    feedbackNo: "No",
    deleteConversation: "Delete conversation",
    privacyPolicy: "Privacy Policy",
    errorMessage: "Something went wrong. Please try again.",
    sessionEndedMessage: "This conversation has ended.",
    unavailableMessage: "The assistant is currently unavailable.",
    findSimilar: "Find similar",
  },
  de: {
    disclaimer: "Sie sprechen mit einem KI-Assistenten",
    askPlaceholder: "Fragen Sie den KI-Assistenten",
    chatPlaceholder: "Was möchten Sie noch wissen?",
    continueBanner: "Vorherige Unterhaltung fortsetzen",
    continueBannerDismiss: "Schließen",
    backToResults: "Zurück zu den Ergebnissen",
    escalationMessage: "Sie können unseren Kundenservice direkt kontaktieren:",
    feedbackQuestion: "War diese Antwort hilfreich?",
    feedbackYes: "Ja",
    feedbackNo: "Nein",
    deleteConversation: "Unterhaltung löschen",
    privacyPolicy: "Datenschutzerklärung",
    errorMessage: "Etwas ist schiefgelaufen. Bitte versuchen Sie es erneut.",
    sessionEndedMessage: "Diese Unterhaltung wurde beendet.",
    unavailableMessage: "Der Assistent ist derzeit nicht verfügbar.",
    findSimilar: "Ähnliche finden",
  },
} as const satisfies TextPacks<ChatbotTexts>;

// A `chatbot` config with its `localization` filled per key from the `language` pack (created
// when absent). Anything that isn't an object (no chatbot, a broken value) is returned as-is,
// as is a non-object `localization`. Never mutates; returns `chatbot` itself when complete.
export function withChatbotTexts<T>(chatbot: T, language: TextLanguage): T {
  if (!isPlainObject(chatbot)) return chatbot;
  const localization = chatbot.localization;
  if (localization !== undefined && !isPlainObject(localization)) return chatbot;
  // A set but blank (or non-string) disclaimer counts as missing (compliance, see the header).
  const disclaimer = localization?.disclaimer;
  const blank = disclaimer !== undefined && !(typeof disclaimer === "string" && disclaimer.trim() !== "");
  const target = blank ? { ...localization, disclaimer: undefined } : localization;
  const filled = fillTexts(target ?? {}, CHATBOT_TEXTS[language]);
  return filled === localization ? chatbot : ({ ...chatbot, localization: filled } as T);
}

// The property of the facade's `dropdownChat` carrying the chat-text merge (see the header).
export const CHAT_TEXTS_PROPERTY = "raventicChatTexts";
export type ChatTextsFiller = typeof withChatbotTexts;

// `ui` (sdk-assistant's `dropdownChat`) plus the chat-text merge. A copy; `ui` is untouched.
export function chatUiWithTexts<U extends object>(ui: U): U {
  return { ...ui, [CHAT_TEXTS_PROPERTY]: withChatbotTexts };
}
